import { apiRequest } from "@/lib/api/http";
import { logCrud } from "@/lib/activity/emit-store";

export interface ClientUser {
  id: string;
  title: string;
  username: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  groups?: string[];
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

type RawGroups = string[] | string | null | undefined;

function normalizeGroups(raw: RawGroups): string[] {
  if (Array.isArray(raw)) return raw.filter((value) => typeof value === "string");
  if (typeof raw !== "string") return [];

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.filter((value) => typeof value === "string");
    }
  } catch {
    // fallback to comma-separated values
  }

  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

export async function listUsers(): Promise<ClientUser[]> {
  const response = await apiRequest<ListResponse<ClientUser>>("/api/users", {
    method: "GET",
    cache: "no-store",
  });
  return response.data.map((user) => ({
    ...user,
    groups: normalizeGroups(user.groups as RawGroups),
  }));
}

/**
 * One user by id. `docs/API.md` does not show this response's envelope, so both a `{ data: {...} }`
 * wrapper and a bare user object are accepted.
 */
export async function getUser(id: string): Promise<ClientUser> {
  const response = await apiRequest<{ data?: ClientUser } & Partial<ClientUser>>(
    `/api/users/${encodeURIComponent(id)}`,
    { method: "GET", cache: "no-store" },
  );
  const raw = (response.data ?? response) as ClientUser;
  return { ...raw, groups: normalizeGroups(raw.groups as RawGroups) };
}

export async function createUser(input: Omit<ClientUser, "id"> & { password: string }) {
  const response = await apiRequest<{ success: true; id: string }>("/api/users", {
    method: "POST",
    body: JSON.stringify(input),
  });
  logCrud("create", "user", input.username ?? "", response.id);
}

export async function updateUser(
  id: string,
  input: Partial<Omit<ClientUser, "id">> & { password?: string },
) {
  await apiRequest<{ success: true }>(`/api/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  logCrud("update", "user", input.username ?? "", id);
}

export async function deleteUser(id: string) {
  await apiRequest<{ success: true }>(`/api/users/${id}`, {
    method: "DELETE",
  });
  logCrud("delete", "user", "", id);
}
